import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { BrandApplication, getMyBrandApplications, withdrawBrandApplication } from '../../api/payment/brand-deal.api';
import { applicationStatusLabel } from '../../utils/brand-deal';
import { IndividualOrdererBadge } from './orderer-badge';
import { BrandThreadToggle } from './brand-thread-panel';
import TurnPill from './turn-pill';
import { sentRequestTurn } from '../../utils/brand-turn';

/** Tab "Đơn của tôi" (creator, M2): đơn xin tham gia sàn, mọi trạng thái + Rút đơn (chỉ khi Pending). */

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export interface BrandMarketApplicationsHandle {
  reload: () => void;
}

// Khai ngoài component cha (landmine ghi nhớ: component khai trong render body gây remount, mất state).
const ApplicationRow: React.FC<{
  application: BrandApplication;
  busy: boolean;
  onWithdraw: () => void;
}> = ({ application, busy, onWithdraw }) => (
  <div className="ui-card flex flex-col gap-2 p-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="ui-heading truncate text-[15px] font-bold">{application.campaignTitle}</div>
        <div className="flex items-center gap-1">
          <div className="ui-muted truncate text-[13px]">{application.brandName}</div>
          <IndividualOrdererBadge kind={application.ordererKind} />
        </div>
      </div>
      <span className="flex shrink-0 items-center gap-2">
        <TurnPill turn={sentRequestTurn(application.status)} />
        <span className="ui-divider inline-flex items-center rounded-full border px-2.5 py-1 text-[12px] font-semibold ui-text">
          {applicationStatusLabel(application.status)}
        </span>
      </span>
    </div>
    {application.message && <p className="ui-text text-[13px]">{application.message}</p>}
    {application.rejectReason && (
      <p className="text-[13px] text-amber-600 dark:text-amber-400">Lý do: {application.rejectReason}</p>
    )}
    <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[12px] ui-muted">
      <span>Gửi lúc: {formatDate(application.createdAt)}</span>
      {application.decidedAt && <span>Cập nhật: {formatDate(application.decidedAt)}</span>}
    </div>
    {application.status === 'Pending' && (
      <div>
        <button
          type="button"
          disabled={busy}
          onClick={onWithdraw}
          className="rounded-lg border border-rose-200 px-3 py-1.5 text-[13px] font-semibold text-rose-600 disabled:opacity-60 dark:border-rose-900/40"
        >
          {busy ? 'Đang rút…' : 'Rút đơn'}
        </button>
      </div>
    )}
    <BrandThreadToggle threadId={application.threadId} />
  </div>
);

const BrandMarketApplications = React.forwardRef<BrandMarketApplicationsHandle>((_, ref) => {
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState<BrandApplication[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const inFlightRef = useRef<Set<string>>(new Set());

  const load = useCallback(() => {
    setLoading(true);
    getMyBrandApplications()
      .then(setApplications)
      .catch(() => toast.error('Không tải được đơn xin tham gia.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  React.useImperativeHandle(ref, () => ({ reload: load }), [load]);

  const handleWithdraw = async (application: BrandApplication) => {
    if (inFlightRef.current.has(application.id)) return;
    if (!window.confirm('Rút đơn xin tham gia này?')) return;
    inFlightRef.current.add(application.id);
    setBusyId(application.id);
    try {
      await withdrawBrandApplication(application.id);
      toast.success('Đã rút đơn.');
      load();
    } catch (err) {
      toast.error(errorMessage(err, 'Không rút được đơn này.'));
    } finally {
      inFlightRef.current.delete(application.id);
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="animate-spin ui-muted" />
      </div>
    );
  }

  if (applications.length === 0) {
    return <p className="ui-muted p-4 text-center text-[14px]">Bạn chưa có đơn xin tham gia nào.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {applications.map((application) => (
        <ApplicationRow
          key={application.id}
          application={application}
          busy={busyId === application.id}
          onWithdraw={() => handleWithdraw(application)}
        />
      ))}
    </div>
  );
});

BrandMarketApplications.displayName = 'BrandMarketApplications';

export default BrandMarketApplications;
